import React from "react";
import { Form, Formik } from "formik";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import * as Yup from "yup";
import { useSnackbar } from "notistack";

import DialogActions from "../../components/DialogActions";
import DialogContent from "../../components/DialogContent";
import LoadingButton from "../../components/ButtonWithLoading";
import { Post } from "../../actions";
import {
  Post_InsertCronJob_URL,
  Post_UpdateCronJob_URL,
} from "../../constants/apiUrls";
import { getTranslation } from "../../heplers/translationHelper";

const emptyJob = {
  name: "",
  appId: "",
  appName: "",
  route: "",
  baseUrl: "",
  execution: 1,
  requiresAccessToken: false,
  retryOnConnectionFailure: true,
  isActive: true,
  description: "",
};

const AddEditCronJob = ({ cronJob, apps, executions, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(cronJob && cronJob.id);

  const initialValues = isEdit
    ? {
        ...emptyJob,
        ...cronJob,
        baseUrl: cronJob.baseUrl || "",
        description: cronJob.description || "",
        name: cronJob.name || "",
      }
    : emptyJob;

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={Yup.object().shape({
        appId: Yup.number()
          .typeError(getTranslation("Required", "Requis", "Erforderlich"))
          .required(getTranslation("Required", "Requis", "Erforderlich")),
        route: Yup.string()
          .required(getTranslation("Required", "Requis", "Erforderlich"))
          .max(300),
        name: Yup.string().max(150),
        baseUrl: Yup.string().max(500),
        description: Yup.string().max(500),
      })}
      onSubmit={(values, { setSubmitting }) => {
        const payload = {
          ...values,
          appId: parseInt(values.appId, 10),
          execution: parseInt(values.execution, 10),
          // The API resolves the name for known apps; sending it keeps a custom app id labelled.
          appName:
            apps?.find((x) => x.value === parseInt(values.appId, 10))?.name ||
            values.appName,
        };

        Post(
          payload,
          isEdit ? Post_UpdateCronJob_URL : Post_InsertCronJob_URL,
          null,
          () => {
            enqueueSnackbar(
              isEdit
                ? getTranslation(
                    "Cron job updated",
                    "Tâche cron mise à jour",
                    "Cron-Job aktualisiert"
                  )
                : getTranslation(
                    "Cron job added",
                    "Tâche cron ajoutée",
                    "Cron-Job hinzugefügt"
                  ),
              { variant: "success" }
            );
            setSubmitting(false);
            onSaved();
          },
          (error) => {
            enqueueSnackbar(error?.data ? error.data : "internal server error", {
              variant: "error",
            });
            setSubmitting(false);
          }
        );
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        setFieldValue,
        isSubmitting,
      }) => (
        <Form>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item sm={12} md={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  variant="outlined"
                  label={getTranslation("App", "Application", "Anwendung")}
                  name="appId"
                  value={values.appId}
                  onChange={handleChange}
                  error={touched.appId && Boolean(errors.appId)}
                  helperText={touched.appId && errors.appId}
                >
                  {apps?.map((app) => (
                    <MenuItem key={app.value} value={app.value}>
                      {app.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item sm={12} md={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  variant="outlined"
                  label={getTranslation("Type", "Type", "Typ")}
                  name="execution"
                  value={values.execution}
                  onChange={handleChange}
                >
                  {executions?.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item sm={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label={getTranslation("Name", "Nom", "Name")}
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
              </Grid>

              <Grid item sm={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label={getTranslation("Route", "Route", "Route")}
                  name="route"
                  placeholder="Cron/reminders"
                  value={values.route}
                  onChange={handleChange}
                  error={touched.route && Boolean(errors.route)}
                  helperText={touched.route && errors.route}
                />
              </Grid>

              <Grid item sm={12} md={12}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label={getTranslation(
                    "Base URL (leave empty to use the configured app URL)",
                    "URL de base (vide pour utiliser l'URL configurée)",
                    "Basis-URL (leer lassen für die konfigurierte App-URL)"
                  )}
                  name="baseUrl"
                  value={values.baseUrl}
                  onChange={handleChange}
                  error={touched.baseUrl && Boolean(errors.baseUrl)}
                  helperText={touched.baseUrl && errors.baseUrl}
                />
              </Grid>

              <Grid item sm={12} md={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  variant="outlined"
                  label={getTranslation(
                    "Description",
                    "Description",
                    "Beschreibung"
                  )}
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  error={touched.description && Boolean(errors.description)}
                  helperText={touched.description && errors.description}
                />
              </Grid>

              <Grid item sm={12} md={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.isActive}
                      onChange={(e, checked) =>
                        setFieldValue("isActive", checked)
                      }
                    />
                  }
                  label={getTranslation("Active", "Actif", "Aktiv")}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.requiresAccessToken}
                      onChange={(e, checked) =>
                        setFieldValue("requiresAccessToken", checked)
                      }
                    />
                  }
                  label={getTranslation(
                    "Requires access token",
                    "Nécessite un jeton d'accès",
                    "Benötigt Zugriffstoken"
                  )}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.retryOnConnectionFailure}
                      onChange={(e, checked) =>
                        setFieldValue("retryOnConnectionFailure", checked)
                      }
                    />
                  }
                  label={getTranslation(
                    "Retry when the app cannot be reached",
                    "Réessayer si l'application est injoignable",
                    "Wiederholen, wenn die App nicht erreichbar ist"
                  )}
                />
                <Typography variant="caption" color="textSecondary" display="block">
                  {getTranslation(
                    "Leave the retry off for jobs that are not safe to run twice, such as invoices or mail runs.",
                    "Désactivez la nouvelle tentative pour les tâches qui ne peuvent pas être exécutées deux fois (factures, envois d'e-mails).",
                    "Wiederholung deaktivieren für Jobs, die nicht zweimal laufen dürfen (Rechnungen, Mailversand)."
                  )}
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <LoadingButton
              color="primary"
              variant="contained"
              type="submit"
              loading={isSubmitting}
              onClick={handleSubmit}
              label={
                isEdit
                  ? getTranslation("Save", "Enregistrer", "Speichern")
                  : getTranslation("Add", "Ajouter", "Hinzufügen")
              }
            />
            <LoadingButton
              variant="outlined"
              onClick={() => onClose()}
              label={getTranslation("Cancel", "Annuler", "Absagen")}
            />
          </DialogActions>
        </Form>
      )}
    </Formik>
  );
};

export default AddEditCronJob;
